import PrintIcon from '@mui/icons-material/Print';
import {
    Box,
    Breadcrumbs,
    Button,
    Link,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, Navigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../api';
import Loading from '../../components/Loading';
import { useFeatureFlag } from '../../helpers/featureFlags';
import { readStoredUser } from '../../helpers/authSession';
import { applyName, applyPhone } from '../../helpers/captureMode';
import { formatCurrencyInCents, formatPhone, presentPhone, resolvePublicMediaUrl } from '../../helpers/formatters';
import {
    formatCalendarDate,
    isConsignmentPartnerModality,
    localISODate,
    partnerModalityLabel,
    partnerMyShareUnitCents,
    partnerPartnerShareUnitCents,
    partnerRetailUnitCents,
} from './partnerLabels';

const VISIT_PAYMENT_METHODS = [
    { value: 'PIX', label: 'PIX' },
    { value: 'Crédito', label: 'Cartão de crédito' },
    { value: 'Débito', label: 'Cartão de débito' },
    { value: 'Dinheiro', label: 'Dinheiro' },
];

const receiptStyles = `
.visit-receipt-page {
  --ink: #1a1a1a;
  --muted: #5c5c5c;
  --line: #d8d8d8;
  --soft: #f6f6f4;
  --accent: #1f4b3a;
  --warn: #8a4b12;
  --warn-bg: #fff6eb;
  --ok: #1b5e20;
  --ok-bg: #e8f5e9;
  --paper: #ffffff;
}
.visit-receipt-page .receipt {
  background: var(--paper);
  border: 1px solid #e4e4e0;
  border-radius: 4px;
  padding: 1.85rem 1.85rem 1.6rem;
  position: relative;
  max-width: 820px;
  margin: 0 auto;
  color: var(--ink);
  font-family: "Source Serif 4", Georgia, "Times New Roman", serif;
}
.visit-receipt-page .receipt::before {
  content: "";
  position: absolute;
  inset: 8px;
  border: 1px solid rgba(31, 75, 58, 0.12);
  pointer-events: none;
}
.visit-receipt-page .receipt-header {
  text-align: center;
  margin-bottom: 1.35rem;
  padding-bottom: 1.1rem;
  border-bottom: 2px solid var(--ink);
}
.visit-receipt-page .store-brand {
  display: flex;
  flex-direction: column;
  gap: 0;
  margin: 0 0 1.65rem;
  background: var(--soft);
  border: 1px solid #e4e4e0;
  border-radius: 8px;
  overflow: hidden;
}
.visit-receipt-page .store-brand-identity {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
  padding: 0.8rem 0.95rem 0.85rem;
}
.visit-receipt-page .store-brand-contacts {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 0.55rem 1rem;
  padding: 0.65rem 0.95rem 0.75rem;
  border-top: 1px solid #e4e4e0;
  background: #fafaf8;
}
.visit-receipt-page .store-logo {
  width: 56px;
  height: 56px;
  object-fit: contain;
  border-radius: 8px;
}
.visit-receipt-page .store-name {
  margin: 0;
  font-family: "IBM Plex Sans", "Helvetica Neue", Arial, sans-serif;
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: var(--accent);
}
.visit-receipt-page .store-contact {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  margin: 0;
  font-family: "IBM Plex Sans", "Helvetica Neue", Arial, sans-serif;
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--muted);
}
.visit-receipt-page .store-contact svg {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}
.visit-receipt-page .receipt-header h2 {
  margin: 0;
  font-size: 1.55rem;
  font-weight: 700;
  letter-spacing: -0.02em;
}
.visit-receipt-page .receipt-meta {
  margin-top: 0.7rem;
  font-family: "IBM Plex Sans", "Helvetica Neue", Arial, sans-serif;
  font-size: 0.82rem;
  color: var(--muted);
  line-height: 1.5;
}
.visit-receipt-page .receipt-meta-line {
  margin: 0;
}
.visit-receipt-page .receipt-badges {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 0.55rem;
  margin-top: 0.9rem;
}
.visit-receipt-page .next-visit-callout {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-top: 0;
  padding: 0.35rem 0.7rem;
  border-radius: 999px;
  background: #e7efeb;
  border: 1px solid rgba(31, 75, 58, 0.22);
  color: var(--accent);
  font-family: "IBM Plex Sans", "Helvetica Neue", Arial, sans-serif;
  font-size: 0.78rem;
  font-weight: 600;
}
.visit-receipt-page .next-visit-callout .label {
  font-size: 0.74rem;
  font-weight: 600;
  opacity: 0.9;
}
.visit-receipt-page .next-visit-callout strong {
  font-size: 0.86rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
.visit-receipt-page .modality-pill {
  display: inline-block;
  margin-top: 0;
  font-family: "IBM Plex Sans", "Helvetica Neue", Arial, sans-serif;
  font-size: 0.68rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 700;
  padding: 0.35rem 0.7rem;
  border-radius: 999px;
  background: #fff4e5;
  border: 1px solid rgba(180, 95, 20, 0.28);
  color: #9a4d0f;
}
.visit-receipt-page .party {
  display: grid;
  gap: 0.4rem;
  margin-bottom: 1.2rem;
  padding: 0.9rem 1rem;
  background: var(--soft);
  border-radius: 6px;
  font-family: "IBM Plex Sans", "Helvetica Neue", Arial, sans-serif;
  font-size: 0.86rem;
}
.visit-receipt-page .party .row {
  display: grid;
  grid-template-columns: 8rem 1fr;
  gap: 0.4rem;
}
.visit-receipt-page .party .label { color: var(--muted); font-weight: 500; }
.visit-receipt-page .party .value { font-weight: 600; }
.visit-receipt-page .product {
  padding: 1rem 0;
  border-bottom: 1px dashed var(--line);
}
.visit-receipt-page .product:last-of-type { border-bottom: 0; }
.visit-receipt-page .product-title {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: baseline;
  margin-bottom: 0.6rem;
}
.visit-receipt-page .product-title h3 {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
}
.visit-receipt-page .badge {
  font-family: "IBM Plex Sans", "Helvetica Neue", Arial, sans-serif;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 0.18rem 0.45rem;
  border-radius: 4px;
  background: #eceff1;
  color: #455a64;
  white-space: nowrap;
}
.visit-receipt-page .grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.35rem 1rem;
  font-family: "IBM Plex Sans", "Helvetica Neue", Arial, sans-serif;
  font-size: 0.84rem;
}
.visit-receipt-page .grid .k { color: var(--muted); }
.visit-receipt-page .grid .v {
  text-align: right;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.visit-receipt-page .highlight {
  margin-top: 0.65rem;
  padding: 0.55rem 0.75rem;
  border-left: 3px solid var(--accent);
  background: #f2f7f4;
  font-family: "IBM Plex Sans", "Helvetica Neue", Arial, sans-serif;
  font-size: 0.84rem;
}
.visit-receipt-page .summary {
  margin-top: 1.25rem;
  padding-top: 1rem;
  border-top: 2px solid var(--ink);
}
.visit-receipt-page .summary h3 {
  margin: 0 0 0.7rem;
  font-size: 0.92rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  font-family: "IBM Plex Sans", "Helvetica Neue", Arial, sans-serif;
}
.visit-receipt-page .summary-table {
  width: 100%;
  border-collapse: collapse;
  font-family: "IBM Plex Sans", "Helvetica Neue", Arial, sans-serif;
  font-size: 0.86rem;
}
.visit-receipt-page .summary-table th,
.visit-receipt-page .summary-table td {
  padding: 0.4rem 0;
  text-align: left;
  border-bottom: 1px solid var(--line);
}
.visit-receipt-page .summary-table td:last-child,
.visit-receipt-page .summary-table th:last-child {
  text-align: right;
  font-variant-numeric: tabular-nums;
  font-weight: 600;
}
.visit-receipt-page .summary-table tr.total td {
  border-bottom: 0;
  padding-top: 0.75rem;
  font-size: 0.98rem;
  font-weight: 700;
}
.visit-receipt-page .payment-box {
  margin-top: 1.2rem;
  padding: 0.85rem 0.95rem;
  border-radius: 6px;
  font-family: "IBM Plex Sans", "Helvetica Neue", Arial, sans-serif;
  font-size: 0.84rem;
}
.visit-receipt-page .payment-box.paid {
  background: var(--ok-bg);
  color: var(--ok);
  border: 1px solid #c8e6c9;
}
.visit-receipt-page .payment-box.unpaid {
  background: var(--warn-bg);
  color: var(--warn);
  border: 1px solid #ffecb3;
}
.visit-receipt-page .payment-box .title {
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  font-size: 0.7rem;
  margin-bottom: 0.35rem;
}
.visit-receipt-page .footer-note {
  margin-top: 1.25rem;
  text-align: center;
  font-family: "IBM Plex Sans", "Helvetica Neue", Arial, sans-serif;
  font-size: 0.7rem;
  color: var(--muted);
  line-height: 1.5;
}
@media (max-width: 640px) {
  .visit-receipt-page .receipt { padding: 1.2rem 1rem 1.1rem; }
  .visit-receipt-page .party .row { grid-template-columns: 1fr; gap: 0.1rem; }
  .visit-receipt-page .grid { grid-template-columns: 1fr; }
  .visit-receipt-page .grid .v { text-align: left; }
  .visit-receipt-page .receipt-badges { flex-direction: column; }
}
@media print {
  body * { visibility: hidden !important; }
  .visit-receipt-page,
  .visit-receipt-page * { visibility: visible !important; }
  .visit-receipt-page {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    background: #fff !important;
  }
  .visit-receipt-page .no-print { display: none !important; }
  .visit-receipt-page .receipt {
    box-shadow: none;
    border: 0;
    border-radius: 0;
    max-width: none;
    padding: 0;
  }
  .visit-receipt-page .receipt::before { display: none; }
  .visit-receipt-page .receipt-header { margin-bottom: 0.95rem; padding-bottom: 0.8rem; }
  .visit-receipt-page .store-brand { margin-bottom: 1.25rem; }
  .visit-receipt-page .store-brand-identity { padding: 0.6rem 0.75rem 0.65rem; }
  .visit-receipt-page .store-brand-contacts { padding: 0.55rem 0.75rem 0.6rem; gap: 0.4rem 0.75rem; }
  .visit-receipt-page .store-logo { width: 48px; height: 48px; }
  .visit-receipt-page .store-name { font-size: 0.88rem; }
  .visit-receipt-page .receipt-header h2 { font-size: 1.35rem; }
  .visit-receipt-page .receipt-meta { margin-top: 0.55rem; }
  .visit-receipt-page .receipt-badges { margin-top: 0.7rem; }
  .visit-receipt-page .party { margin-bottom: 0.9rem; padding: 0.7rem 0.8rem; gap: 0.35rem; }
  .visit-receipt-page .product { padding: 0.75rem 0; }
  .visit-receipt-page .product-title { margin-bottom: 0.5rem; }
  .visit-receipt-page .grid { gap: 0.3rem 1rem; }
  .visit-receipt-page .highlight { margin-top: 0.55rem; }
  .visit-receipt-page .summary { margin-top: 0.9rem; padding-top: 0.8rem; }
  .visit-receipt-page .summary h3 { margin-bottom: 0.55rem; }
  .visit-receipt-page .summary-table th,
  .visit-receipt-page .summary-table td { padding: 0.32rem 0; }
  .visit-receipt-page .summary-table tr.total td { padding-top: 0.6rem; }
  .visit-receipt-page .payment-box { margin-top: 0.9rem; padding: 0.65rem 0.8rem; }
  .visit-receipt-page .footer-note { margin-top: 0.9rem; }
}
`;

function buildReceiptFromVisit(visit) {
    if (!visit) return null;
    const stored = (visit.receipt && typeof visit.receipt === 'object') ? visit.receipt : null;
    const modality = stored?.modality || visit.partner_modality || '';
    const isConsignment = isConsignmentPartnerModality(modality);
    const itemsByProduct = Object.fromEntries(
        (visit.partner_items || []).map((item) => [item.product_uuid, item]),
    );

    // Sempre recalcula pelos vendidos da visita + preços do acordo.
    // Pedido (register_sale) não entra no comprovante.
    const sourceLines = (visit.lines && visit.lines.length > 0)
        ? visit.lines
        : (stored?.products || []);

    const products = sourceLines.map((line) => {
        const productUuid = line.product_uuid || '';
        const item = itemsByProduct[productUuid] || {};
        const qty = Number(line.sold_qty) || 0;
        const restocked = Number(line.restocked_qty) || 0;
        const retail = partnerRetailUnitCents(item) || Number(line.unit_price_cents) || 0;
        const agreed = partnerMyShareUnitCents(item);
        const discount = partnerPartnerShareUnitCents(item);
        const myShare = (isConsignment ? agreed : discount) * qty;
        const partnerShare = (isConsignment ? discount : agreed) * qty;
        const soldTotal = retail * qty;
        const settleCents = isConsignment ? myShare : partnerShare;
        return {
            product_uuid: productUuid,
            name: line.product_name || line.name || item.product_name || productUuid,
            sold_qty: qty,
            restocked_qty: restocked,
            register_sale_qty: Number(line.register_sale_qty) || 0,
            unit_price_cents: retail,
            sold_total_cents: soldTotal,
            my_share_cents: myShare,
            partner_share_cents: partnerShare,
            settle_cents: settleCents,
            verb: isConsignment ? 'receber' : 'repassar',
            prep: isConsignment ? 'do' : 'ao',
        };
    }).filter((p) => p.sold_qty > 0 || p.restocked_qty > 0);

    const totals = products.reduce(
        (acc, p) => {
            acc.sold_qty += Number(p.sold_qty) || 0;
            acc.sold_cents += Number(p.sold_total_cents) || 0;
            acc.restocked_qty += Number(p.restocked_qty) || 0;
            acc.my_share_cents += Number(p.my_share_cents) || 0;
            acc.partner_share_cents += Number(p.partner_share_cents) || 0;
            acc.settle_cents += Number(p.settle_cents) || 0;
            return acc;
        },
        {
            sold_qty: 0,
            sold_cents: 0,
            restocked_qty: 0,
            my_share_cents: 0,
            partner_share_cents: 0,
            settle_cents: 0,
        },
    );

    return {
        visited_on: stored?.visited_on || visit.visited_on || visit.planned_on || '',
        modality,
        partner_name: stored?.partner_name || visit.partner_name || '',
        partner_contact_name: stored?.partner_contact_name || visit.partner_contact_name || '',
        partner_contact_phone: stored?.partner_contact_phone || visit.partner_contact_phone || '',
        products,
        totals: {
            ...totals,
            settle_label: isConsignment
                ? 'Total a receber do estabelecimento'
                : 'Total a repassar ao estabelecimento',
            settle_verb: isConsignment ? 'receber' : 'repassar',
            settle_prep: isConsignment ? 'do' : 'ao',
        },
        payment: stored?.payment || { paid: false, method: '', paid_on: '' },
        issued_at: stored?.issued_at || new Date().toISOString(),
        next_visit_on: stored?.next_visit_on || '',
    };
}

const WhatsAppIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
        <path d="M12.04 2C6.58 2 2.15 6.4 2.15 11.83c0 1.99.59 3.84 1.61 5.4L2 22l4.95-1.63a9.86 9.86 0 004.99 1.35h.01c5.46 0 9.89-4.4 9.89-9.84C21.84 6.4 17.5 2 12.04 2zm5.74 13.98c-.24.68-1.4 1.25-1.93 1.33-.49.07-1.12.1-1.81-.11-.42-.13-.96-.31-1.65-.61-2.9-1.25-4.79-4.17-4.93-4.36-.14-.19-1.15-1.53-1.15-2.92s.73-2.07.99-2.35c.26-.28.56-.35.75-.35h.54c.17 0 .4-.06.63.48.24.56.8 1.94.87 2.08.07.14.12.31.02.5-.1.19-.14.31-.28.48-.14.17-.29.38-.42.51-.14.14-.28.29-.12.56.16.28.71 1.17 1.53 1.9 1.05.94 1.94 1.23 2.22 1.37.28.14.44.12.6-.07.17-.19.7-.81.88-1.09.19-.28.37-.23.63-.14.26.09 1.64.77 1.92.91.28.14.47.21.54.33.07.12.07.7-.17 1.38z" />
    </svg>
);

const InstagramIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
        <path d="M7 2h10a5 5 0 015 5v10a5 5 0 01-5 5H7a5 5 0 01-5-5V7a5 5 0 015-5zm0 2a3 3 0 00-3 3v10a3 3 0 003 3h10a3 3 0 003-3V7a3 3 0 00-3-3H7zm5 3.5A4.5 4.5 0 1112 16.5 4.5 4.5 0 0112 7.5zm0 2A2.5 2.5 0 1014.5 12 2.5 2.5 0 0012 9.5zm5.25-3.25a1 1 0 11-1 1 1 1 0 011-1z" />
    </svg>
);

const TikTokIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
        <path d="M14.5 3c.4 2.3 1.8 3.9 4 4.3v2.3c-1.4-.1-2.7-.5-3.8-1.3v6.2c0 3.2-2.5 5.5-5.7 5.5S3.3 17.7 3.3 14.5 5.8 9 9 9c.3 0 .6 0 .9.1v2.4a3.3 3.3 0 00-.9-.1c-1.8 0-3.2 1.4-3.2 3.1s1.4 3.1 3.2 3.1 3.2-1.4 3.2-3.1V3h2.3z" />
    </svg>
);

function socialHandle(value) {
    const raw = String(value || '').trim();
    if (!raw) return '';
    return raw.replace(/^@+/, '');
}

function PartnerVisitReceiptPage() {
    const { visitUuid } = useParams();
    const { enabled, loading: flagLoading } = useFeatureFlag('commerce_partners');
    const [visit, setVisit] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState('PIX');
    const [paymentDate, setPaymentDate] = useState(localISODate());

    const load = () => {
        setLoading(true);
        return api.get(`/commerce-partners/visits/${visitUuid}`)
            .then((response) => {
                const data = response?.data?.data || null;
                setVisit(data);
                if (data?.visited_on) {
                    setPaymentDate(String(data.visited_on).slice(0, 10));
                }
            })
            .catch(() => setVisit(null))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (!enabled) return undefined;
        load();
        return undefined;
    }, [enabled, visitUuid]);

    const receipt = useMemo(() => buildReceiptFromVisit(visit), [visit]);
    const paid = Boolean(receipt?.payment?.paid);
    const isConsignment = isConsignmentPartnerModality(receipt?.modality || visit?.partner_modality);

    const handlePay = async () => {
        if (!paymentMethod || !paymentDate) {
            Swal.fire('Atenção', 'Informe a data e a forma de pagamento.', 'warning');
            return;
        }
        setSaving(true);
        try {
            const response = await api.post(`/commerce-partners/visits/${visitUuid}/pay`, {
                payment_method: paymentMethod,
                payment_date: paymentDate,
            });
            if (!response?.data?.success) {
                Swal.fire('Atenção', Object.values(response?.data?.errors || {})[0] || response?.data?.error || 'Falha.', 'warning');
                return;
            }
            setVisit(response.data.data);
            Swal.fire('OK', 'Pagamento registrado.', 'success');
        } catch (error) {
            Swal.fire('Erro', error?.response?.data?.error || 'Falha ao registrar pagamento.', 'error');
        } finally {
            setSaving(false);
        }
    };

    if (flagLoading || loading) return <Loading />;
    if (!enabled) return <Navigate to="/admin" replace />;
    if (!visit || visit.status !== 'done' || !receipt) {
        return (
            <Box>
                <Typography>Comprovante indisponível. Conclua a visita primeiro.</Typography>
                <Button component={RouterLink} to={`/admin/partners/visits/${visitUuid}`} sx={{ mt: 2 }}>
                    Voltar à visita
                </Button>
            </Box>
        );
    }

    const issuedAt = receipt.issued_at
        ? new Date(receipt.issued_at).toLocaleString('pt-BR')
        : new Date().toLocaleString('pt-BR');
    const settleLabel = receipt.totals?.settle_label
        || (isConsignment ? 'Total a receber do estabelecimento' : 'Total a repassar ao estabelecimento');
    const user = readStoredUser() || {};
    const storeName = (user.store_name || user.name || '').trim();
    const storePhone = applyPhone(formatPhone(user.whatsapp || user.phone || ''));
    const storeInstagram = socialHandle(user.instagram);
    const storeTiktok = socialHandle(user.tiktok);
    const logoUrl = user.profile_picture ? resolvePublicMediaUrl(user.profile_picture) : '';
    const hasIdentity = Boolean(logoUrl || storeName);
    const hasContacts = Boolean(storePhone || storeInstagram || storeTiktok);

    return (
        <Box className="visit-receipt-page">
            <style>{receiptStyles}</style>
            <link
                rel="stylesheet"
                href="https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=Source+Serif+4:opsz,wght@8..60,500;8..60,700&display=swap"
            />

            <Box className="no-print" sx={{ mb: 2 }}>
                <Breadcrumbs sx={{ mb: 1 }}>
                    <Link component={RouterLink} to="/admin/partners" underline="hover" color="inherit">Parcerias</Link>
                    <Link component={RouterLink} to={`/admin/partners/${visit.partner_uuid}`} underline="hover" color="inherit">
                        {applyName(visit.partner_name)}
                    </Link>
                    <Link component={RouterLink} to={`/admin/partners/visits/${visitUuid}`} underline="hover" color="inherit">
                        Visita
                    </Link>
                    <Typography color="text.primary">Comprovante</Typography>
                </Breadcrumbs>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center" justifyContent="space-between">
                    <Typography variant="h5">Comprovante de visita</Typography>
                    <Button variant="contained" startIcon={<PrintIcon />} onClick={() => window.print()}>
                        Imprimir
                    </Button>
                </Stack>
            </Box>

            <article className="receipt">
                <header className="receipt-header">
                    {(hasIdentity || hasContacts) ? (
                        <div className="store-brand">
                            {hasIdentity ? (
                                <div className="store-brand-identity">
                                    {logoUrl ? (
                                        <img className="store-logo" src={logoUrl} alt={storeName || 'Logo'} />
                                    ) : null}
                                    {storeName ? <p className="store-name">{storeName}</p> : null}
                                </div>
                            ) : null}
                            {hasContacts ? (
                                <div className="store-brand-contacts">
                                    {storePhone ? (
                                        <p className="store-contact">
                                            <WhatsAppIcon />
                                            <span>{storePhone}</span>
                                        </p>
                                    ) : null}
                                    {storeInstagram ? (
                                        <p className="store-contact">
                                            <InstagramIcon />
                                            <span>@{storeInstagram}</span>
                                        </p>
                                    ) : null}
                                    {storeTiktok ? (
                                        <p className="store-contact">
                                            <TikTokIcon />
                                            <span>@{storeTiktok}</span>
                                        </p>
                                    ) : null}
                                </div>
                            ) : null}
                        </div>
                    ) : null}
                    <h2>Comprovante de visita</h2>
                    <div className="receipt-meta">
                        <p className="receipt-meta-line">
                            Data da visita: <strong>{formatCalendarDate(receipt.visited_on || visit.visited_on)}</strong>
                            {' · '}
                            Emitido em <span>{issuedAt}</span>
                        </p>
                        <div className="receipt-badges">
                            {receipt.next_visit_on ? (
                                <div className="next-visit-callout">
                                    <span className="label">Próxima visita agendada para</span>
                                    <strong>{formatCalendarDate(receipt.next_visit_on)}</strong>
                                </div>
                            ) : null}
                            <div className="modality-pill">
                                Modalidade: {partnerModalityLabel(receipt.modality || visit.partner_modality)}
                            </div>
                        </div>
                    </div>
                </header>

                <section className="party">
                    <div className="row">
                        <span className="label">Estabelecimento</span>
                        <span className="value">{applyName(receipt.partner_name || visit.partner_name) || '—'}</span>
                    </div>
                    <div className="row">
                        <span className="label">Responsável</span>
                        <span className="value">{applyName(receipt.partner_contact_name || visit.partner_contact_name) || '—'}</span>
                    </div>
                    <div className="row">
                        <span className="label">Telefone</span>
                        <span className="value">{presentPhone(receipt.partner_contact_phone || visit.partner_contact_phone) || '—'}</span>
                    </div>
                </section>

                <section className="products">
                    {(receipt.products || []).map((product, index) => (
                        <article className="product" key={`${product.name}-${index}`}>
                            <div className="product-title">
                                <h3>{product.name}</h3>
                                {Number(product.restocked_qty) > 0 ? (
                                    <span className="badge">Reposição: {product.restocked_qty}</span>
                                ) : null}
                            </div>
                            <div className="grid">
                                <div className="k">Vendas desde a última visita</div>
                                <div className="v">{product.sold_qty ?? product.register_sale_qty ?? 0}</div>
                                <div className="k">Valor unitário</div>
                                <div className="v">{formatCurrencyInCents(product.unit_price_cents || 0)}</div>
                                <div className="k">Total em vendas</div>
                                <div className="v">{formatCurrencyInCents(product.sold_total_cents || 0)}</div>
                                <div className="k">Valor a recolher</div>
                                <div className="v">{formatCurrencyInCents(product.my_share_cents || 0)}</div>
                                <div className="k">Valor do estabelecimento</div>
                                <div className="v">{formatCurrencyInCents(product.partner_share_cents || 0)}</div>
                            </div>
                            <div className="highlight">
                                Valor a <strong>{product.verb || receipt.totals?.settle_verb}</strong>{' '}
                                <strong>{product.prep || receipt.totals?.settle_prep}</strong> estabelecimento:{' '}
                                <strong>{formatCurrencyInCents(product.settle_cents || 0)}</strong>
                            </div>
                        </article>
                    ))}
                </section>

                <section className="summary">
                    <h3>Resumo da visita</h3>
                    <table className="summary-table">
                        <tbody>
                            <tr>
                                <th>Data da visita</th>
                                <td>{formatCalendarDate(receipt.visited_on || visit.visited_on)}</td>
                            </tr>
                            <tr>
                                <th>Total de itens vendidos</th>
                                <td>{receipt.totals?.sold_qty || 0}</td>
                            </tr>
                            <tr>
                                <th>Valor total vendido</th>
                                <td>{formatCurrencyInCents(receipt.totals?.sold_cents || 0)}</td>
                            </tr>
                            <tr>
                                <th>Valor a recolher</th>
                                <td>{formatCurrencyInCents(receipt.totals?.my_share_cents || 0)}</td>
                            </tr>
                            <tr>
                                <th>Valor do estabelecimento</th>
                                <td>{formatCurrencyInCents(receipt.totals?.partner_share_cents || 0)}</td>
                            </tr>
                            <tr>
                                <th>Reposição nesta visita</th>
                                <td>
                                    {Number(receipt.totals?.restocked_qty) > 0
                                        ? `Sim · ${receipt.totals.restocked_qty} unidades`
                                        : 'Não'}
                                </td>
                            </tr>
                            <tr className="total">
                                <td>{settleLabel}</td>
                                <td>{formatCurrencyInCents(receipt.totals?.settle_cents || 0)}</td>
                            </tr>
                        </tbody>
                    </table>
                </section>

                <section className={`payment-box ${paid ? 'paid' : 'unpaid'}`}>
                    <div className="title">{paid ? 'Pagamento realizado' : 'Pagamento pendente'}</div>
                    <div>
                        {paid ? (
                            <>
                                {isConsignment ? 'Recebido do' : 'Repassado ao'} estabelecimento em{' '}
                                <strong>{formatCalendarDate(receipt.payment?.paid_on)}</strong>
                                {receipt.payment?.method ? (
                                    <>
                                        {' '}via <strong>{receipt.payment.method}</strong>
                                    </>
                                ) : null}
                                .
                            </>
                        ) : (
                            <>
                                <strong>Atenção:</strong> o valor a{' '}
                                {isConsignment ? 'receber do' : 'repassar ao'} estabelecimento (
                                <strong>{formatCurrencyInCents(receipt.totals?.settle_cents || 0)}</strong>
                                ) ainda não foi pago.
                            </>
                        )}
                    </div>
                </section>

                <footer className="footer-note">
                    Documento gerado para registro da visita comercial entre as partes.
                    <br />
                    Valores calculados conforme a modalidade do acordo vigente.
                </footer>
            </article>

            {!paid ? (
                <Paper className="no-print" sx={{ p: 2, mt: 2, maxWidth: 820, mx: 'auto' }}>
                    <Typography variant="h6" sx={{ mb: 1.5 }}>Informar pagamento</Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="flex-start">
                        <TextField
                            type="date"
                            label="Data do pagamento"
                            value={paymentDate}
                            onChange={(e) => setPaymentDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ minWidth: 200 }}
                        />
                        <TextField
                            select
                            label="Forma de pagamento"
                            value={paymentMethod}
                            onChange={(e) => setPaymentMethod(e.target.value)}
                            sx={{ minWidth: 220 }}
                        >
                            {VISIT_PAYMENT_METHODS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                        </TextField>
                        <Button variant="contained" onClick={handlePay} disabled={saving}>
                            {saving ? 'Salvando…' : 'Registrar pagamento'}
                        </Button>
                    </Stack>
                </Paper>
            ) : null}
        </Box>
    );
}

export default PartnerVisitReceiptPage;
